"use client"

import { useState, useContext } from "react"
import { useNavigate } from "react-router-dom"
import api from "../../services/api"
import { AuthContext } from "../../context/AuthContext"

export default function LoginForm() {
  const { login } = useContext(AuthContext)
  const navigate = useNavigate()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)

  const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);
  setError("");

  try {
    const res = await api.post("/auth/login", { email, password });
    const { token, user } = res.data.data || {};

    if (!token || !user) {
      setError(res.data?.message || "Login failed");
      return;
    }

    // Store in AuthContext
    login(token, user);

    // Redirect based on role
    if (user.role === "admin" || user.role === "super_admin") {
      navigate("/admin/dashboard"); // Admin dashboard
    } else {
      navigate("/user-dashboard"); // Regular user dashboard
    }
  } catch (err) {
    setError(err.response?.data?.message || "Login failed");
  } finally {
    setLoading(false);
  }
};

  return (
    <>
      <style>{`
        .login-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #FFFFFF 0%, #F3F4F6 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          position: relative;
          overflow: hidden;
        }
        
        /* Updated floating orbs to lighter colors for white background */
        .login-container::before {
          content: '';
          position: absolute;
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(79, 70, 229, 0.08) 0%, transparent 70%);
          top: -250px;
          right: -250px;
          border-radius: 50%;
        }
        
        .login-container::after {
          content: '';
          position: absolute;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(34, 197, 94, 0.06) 0%, transparent 70%);
          bottom: -200px;
          left: -200px;
          border-radius: 50%;
        }
        
        /* Updated card to white with subtle border */
        .login-card {
          background: #FFFFFF;
          backdrop-filter: blur(20px);
          border: 1px solid rgba(79, 70, 229, 0.15);
          border-radius: 20px;
          padding: 48px;
          max-width: 450px;
          width: 100%;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
          position: relative;
          z-index: 1;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        
        .login-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 25px 70px rgba(0, 0, 0, 0.12);
        }
        
        /* Updated text colors to dark for readability */
        .login-title {
          color: #1F2937;
          font-size: 32px;
          font-weight: 700;
          margin-bottom: 8px;
          text-align: center;
        }
        
        .login-subtitle {
          color: #6B7280;
          font-size: 14px;
          text-align: center;
          margin-bottom: 32px;
        }
        
        .form-group-custom {
          position: relative;
          margin-bottom: 20px;
        }
        
        .form-group-custom .icon {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          color: #9CA3AF;
          z-index: 2;
        }
        
        /* Updated input backgrounds to light gray */
        .form-control-custom {
          background: #F9FAFB;
          border: 1px solid #E5E7EB;
          border-radius: 12px;
          padding: 14px 16px 14px 48px;
          color: #1F2937;
          font-size: 15px;
          width: 100%;
          transition: all 0.3s ease;
        }
        
        .form-control-custom:focus {
          outline: none;
          background: #FFFFFF;
          border-color: #0d25d8;
          box-shadow: 0 0 0 3px rgba(70, 94, 229, 0.1);
        }
        
        .form-control-custom::placeholder {
          color: #9CA3AF;
        }
        
        .password-toggle {
          position: absolute;
          right: 16px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #9CA3AF;
          cursor: pointer;
          padding: 4px;
          transition: color 0.2s;
        }
        
        .password-toggle:hover {
          color: #0900b9da;
        }
        
        .remember-forgot {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }
        
        .remember-me {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .remember-me input[type="checkbox"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
          accent-color: #4F46E5;
        }
        
        .remember-me label {
          color: #6B7280;
          font-size: 14px;
          cursor: pointer;
          margin: 0;
        }
        
        .forgot-link {
          color: #1d32d3d9;
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          transition: color 0.2s;
        }
        
        .forgot-link:hover {
          color: rgba(99, 101, 241, 0.77)d9;
        }
        
        .btn-login {
          background: linear-gradient(135deg, #1130df 0%, #171b23 100%);
          border: none;
          border-radius: 12px;
          padding: 14px;
          color: white;
          font-weight: 600;
          font-size: 16px;
          width: 100%;
          transition: all 0.3s ease;
          box-shadow: 0 4px 20px rgba(28, 64, 207, 0.46);
        }
        
        .btn-login:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 25px rgba(16, 34, 233, 0.4);
        }
        
        .btn-login:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .divider {
          display: flex;
          align-items: center;
          margin: 24px 0;
          color: #9CA3AF;
          font-size: 14px;
        }
        
        .divider::before,
        .divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: #E5E7EB;
        }
        
        .divider span {
          padding: 0 16px;
        }
        
        .social-login {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin-bottom: 24px;
        }
        
        /* Updated social buttons to light background */
        .btn-social {
          background: #F9FAFB;
          border: 1px solid #E5E7EB;
          border-radius: 12px;
          padding: 12px;
          color: #1F2937;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        
        .btn-social:hover {
          background: #FFFFFF;
          border-color: #4F46E5;
        }
        
        .signup-link {
          text-align: center;
          margin-top: 24px;
          color: #6B7280;
          font-size: 14px;
        }
        
        .signup-link a {
          color: #1d32d3d9;
          text-decoration: none;
          font-weight: 600;
          transition: color 0.2s;
        }
        
        .signup-link a:hover {
          color: #09146cab;
        }
        
        .alert-custom {
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 12px;
          padding: 12px 16px;
          color: #DC2626;
          font-size: 14px;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .spinner {
          width: 18px;
          height: 18px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div className="login-container">
        <div className="login-card">
          <h1 className="login-title">Welcome Back</h1>
          <p className="login-subtitle">Enter your credentials to access your account</p>

          {error && (
            <div className="alert-custom">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group-custom">
              <svg className="icon" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
              </svg>
              <input
                type="email"
                className="form-control-custom"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="form-group-custom">
              <svg className="icon" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                  clipRule="evenodd"
                />
              </svg>
              <input
                type={showPassword ? "text" : "password"}
                className="form-control-custom"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z"
                      clipRule="evenodd"
                    />
                    <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path
                      fillRule="evenodd"
                      d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            </div>

            <div className="remember-forgot">
              <div className="remember-me">
                <input
                  type="checkbox"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <label htmlFor="rememberMe">Remember me</label>
              </div>
              <a
                href="/forgot-password"
                // onClick={(e) => {
                //   e.preventDefault()
                //   handleForgotPassword()
                // }}
                className="forgot-link"
              >
                Forgot password?
              </a>
            </div>

            <button type="submit" className="btn-login" disabled={loading}>
              {loading ? (
                <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                  <span className="spinner"></span>
                  Logging in...
                </span>
              ) : (
                "Sign In"
              )}
            </button>
          </form>

          {/* <div className="divider">
            <span>or continue with</span>
          </div>

          <div className="social-login">
            <button className="btn-social">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor">
                <path d="M9 0a9 9 0 00-2.84 17.54c.45.08.62-.2.62-.44v-1.54c-2.51.55-3.04-1.21-3.04-1.21-.41-1.04-1-1.32-1-1.32-.82-.56.06-.55.06-.55.9.07 1.38.93 1.38.93.8 1.38 2.1.98 2.62.75.08-.58.31-.98.57-1.2-2-.23-4.1-1-4.1-4.45 0-.98.35-1.79.93-2.42-.09-.23-.4-1.14.09-2.38 0 0 .76-.24 2.48.92a8.58 8.58 0 014.52 0c1.72-1.16 2.48-.92 2.48-.92.49 1.24.18 2.15.09 2.38.58.63.93 1.44.93 2.42 0 3.46-2.1 4.22-4.11 4.44.32.28.61.83.61 1.67v2.47c0 .24.17.52.63.44A9 9 0 009 0z" />
              </svg>
              Google
            </button>
            <button className="btn-social">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor">
                <path d="M9 0a9 9 0 00-2.84 17.54c.45.08.62-.2.62-.44v-1.54c-2.51.55-3.04-1.21-3.04-1.21-.41-1.04-1-1.32-1-1.32-.82-.56.06-.55.06-.55.9.07 1.38.93 1.38.93.8 1.38 2.1.98 2.62.75.08-.58.31-.98.57-1.2-2-.23-4.1-1-4.1-4.45 0-.98.35-1.79.93-2.42-.09-.23-.4-1.14.09-2.38 0 0 .76-.24 2.48.92a8.58 8.58 0 014.52 0c1.72-1.16 2.48-.92 2.48-.92.49 1.24.18 2.15.09 2.38.58.63.93 1.44.93 2.42 0 3.46-2.1 4.22-4.11 4.44.32.28.61.83.61 1.67v2.47c0 .24.17.52.63.44A9 9 0 009 0z" />
              </svg>
              GitHub
            </button>
          </div> */}

          <div className="signup-link">
            Don't have an account? <a href="/register">Sign up</a>
          </div>
        </div>
      </div>
    </>
  )
}
